import { useTranslate } from "@similarweb/i18n-provider";
import type { SubtitleFilter } from "@similarweb/pro-logic-components";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { renderIndustryDataAvailabilityNotice } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/IndustryDataAvailabilityBanner";
import { useDurationSubtitleFilter } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDurationSubtitleFilter";
import type { FlattenedEntitiesData } from "app/pages/digital-marketing/ad-intelligence/common/overview/hooks/useActivityBreakdownData";
import { PerformanceEntitiesWidget } from "app/pages/digital-marketing/ad-intelligence/common/overview/PerformanceEntitiesWidget";
import { PerformanceOverviewWidget } from "app/pages/digital-marketing/ad-intelligence/common/overview/PerformanceOverviewWidget";
import {
    Divider,
    PerformanceEntitiesWidgetContainer,
    PerformanceWidgetContainer,
} from "app/pages/digital-marketing/ad-intelligence/common/overview/performanceShared";
import { ShortEmptyState } from "app/pages/digital-marketing/ad-intelligence/common/overview/shortEmptyState";

import { useIndustryActivity } from "./useIndustryActivity";

interface IIndustryTrackedActivityProps {
    category: IFlattenedCategory;
}

const EMPTY_ENTITIES_DATA: FlattenedEntitiesData<"advertiser"> = {
    favicon: "",
    campaigns: { count: 0, change: 0 },
    creatives: { count: 0, change: 0 },
    publishers: { count: 0, change: 0 },
};

const isEmptyPerformance = (data: ReturnType<typeof useIndustryActivity>["performanceData"]) =>
    !data || (!data.spend.value && !data.impressions.value && !data.visits?.value);

const isEmptyEntities = (data: ReturnType<typeof useIndustryActivity>["entitiesData"]) =>
    !data || (!data.campaigns.count && !data.creatives.count && !data.publishers?.count);

export const IndustryTrackedActivity = ({ category }: IIndustryTrackedActivityProps) => {
    const { translate } = useTranslate();
    const { performanceData, entitiesData, isWindow, isLoading, isError, availability } = useIndustryActivity(category);

    const durationSubtitleFilter = useDurationSubtitleFilter();
    const subtitleFilters = useSubtitleFilters("country", "webSource");
    const combinedSubtitleFilters: SubtitleFilter[] = [durationSubtitleFilter, ...subtitleFilters];

    const showPerformanceEmptyState = !isLoading && isEmptyPerformance(performanceData);
    const showEntitiesEmptyState = !isLoading && isEmptyEntities(entitiesData);

    return (
        <WidgetContainer
            title={translate("ad.intelligence.advertiser.overview.performance.widget.title")}
            tooltip={translate("ad.intelligence.advertiser.overview.performance.widget.title.tooltip")}
            subtitleFilters={combinedSubtitleFilters}
            withBorder
            automationId="industry-tracked-activity-widget"
            noticeSlot={renderIndustryDataAvailabilityNotice(availability)}
        >
            <PerformanceWidgetContainer>
                {showPerformanceEmptyState ? (
                    <ShortEmptyState
                        isError={isError}
                        minHeight={98}
                    />
                ) : (
                    <PerformanceOverviewWidget
                        data={performanceData}
                        isLoading={isLoading}
                        controller="advertiser"
                        isWindow={isWindow}
                    />
                )}
                <Divider />
                <PerformanceEntitiesWidgetContainer>
                    {showEntitiesEmptyState ? (
                        <ShortEmptyState
                            isError={isError}
                            minHeight={98}
                        />
                    ) : (
                        <PerformanceEntitiesWidget
                            data={entitiesData ?? EMPTY_ENTITIES_DATA}
                            isLoading={isLoading}
                            controller="advertiser"
                            isWindow={isWindow}
                        />
                    )}
                </PerformanceEntitiesWidgetContainer>
            </PerformanceWidgetContainer>
        </WidgetContainer>
    );
};
